import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import GoogleBusinessWorkspace from '../GoogleBusinessWorkspace';
import SocialConnectButton, {
  socialConnectDescription,
  type ConnectSocialPlatform,
} from '../SocialConnectButton';
import TwilioSettings from '../settings/TwilioSettings';
import { PLATFORM_ICON_SRC } from '../PlatformIcon';

export type MarketingConnectTarget = 'gbp' | ConnectSocialPlatform | 'twilio';

/**
 * Opens the existing platform connection UI on top of Marketing, so a
 * disconnected Platforms row connects here instead of sending the user to
 * Nexus Connect. OAuth return URLs are chosen by the connect buttons from
 * the current page.
 */
export default function MarketingPlatformConnectModal({
  target,
  autoStart = false,
  onClose,
  onChanged,
}: {
  target: MarketingConnectTarget | null;
  autoStart?: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const notify = useCallback(() => {
    onChangedRef.current?.();
  }, []);

  if (!target) return null;

  if (target === 'twilio') {
    return (
      <TwilioSettings
        isOpen
        onClose={onClose}
        onSave={notify}
      />
    );
  }

  const title =
    target === 'gbp'
      ? 'Google Business Profile'
      : target === 'facebook'
        ? 'Facebook'
        : target === 'instagram'
          ? 'Instagram'
          : 'LinkedIn';
  const subtitle =
    target === 'gbp'
      ? 'Connect your profile for reviews, posts, and photos'
      : target === 'linkedin'
        ? 'Connect a personal profile or company page'
        : `Connect ${title} to publish and automate posts`;
  const icon =
    target === 'gbp' ? PLATFORM_ICON_SRC.google_business : PLATFORM_ICON_SRC[target];

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[20px]"
        style={{ zIndex: 100120 }}
        onClick={onClose}
      />
      <div
        className="fixed inset-0 flex items-center justify-center pointer-events-none"
        style={{ zIndex: 100121 }}
      >
        <div
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-lg sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60 bg-white">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img
                src={icon}
                alt=""
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl shadow-sm flex-shrink-0 object-contain bg-white"
                draggable={false}
              />
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">{title}</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{subtitle}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50 space-y-4">
            {target === 'gbp' ? (
              <GoogleBusinessWorkspace
                mode="connection"
                autoStartConnect={autoStart}
                onConnectionChange={notify}
              />
            ) : (
              <>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {socialConnectDescription(target)}
                </p>
                <div className="bg-white border border-gray-200 rounded-2xl p-4">
                  <SocialConnectButton
                    platform={target}
                    size="large"
                    autoStart={autoStart}
                    onConnectionChange={notify}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
